/**
 * Sync manager for syncing offline changes with the server
 */

import { localDB, type SyncQueueItem } from '../../db/client'
import { httpClient, type ApiError, isTimeoutError, type HttpClient } from '../../api/http'
import { realtimeService } from '../../api/signalr'
import { leaderElection } from '../../api/leader-election'
import type { CommandResponse } from '../commands/types'
import { logger } from '../utils/logger'
import { type EventPayload } from '../events/processor'
import { memoryStateService } from '../state/memory-store'
import { connectionStateService, type SyncStatus, type ConnectionStateService } from './connection-state-service'
import { tabSyncCoordinator, type TabSyncCoordinator } from '../coordination/tab-sync-coordinator'

const MAX_CONFLICT_RETRIES = Number.MAX_SAFE_INTEGER // Conflicts get essentially unlimited retries
const MAX_UNKNOWN_RETRIES = Number.MAX_SAFE_INTEGER // Unknown errors get essentially unlimited retries
const UNLIMITED_RETRIES = Number.MAX_SAFE_INTEGER // Network/server errors retry indefinitely

// Exponential backoff settings
const BASE_RETRY_DELAY = 1000 // 1 second
const MAX_RETRY_DELAY = 60000 // 60 seconds

const ErrorType = {
  Conflict: 'conflict' as const,       // 409 - concurrent update, retry with backoff
  NotFound: 'not_found' as const,      // 404 - depends on operation
  BadRequest: 'bad_request' as const,  // 400 - invalid data, immediate failure
  ServerError: 'server_error' as const, // 500, 503 - retry indefinitely
  NetworkError: 'network_error' as const, // Connection issues, retry indefinitely
  Unknown: 'unknown' as const          // Unknown errors, moderate retries
} as const

type ErrorType = typeof ErrorType[keyof typeof ErrorType]

type HydrationCoordinatorLike = {
  ensureScenarioReady: (scenarioId: string, priority: 'high' | 'background') => Promise<void>
}

export class ServerSyncService {
  private isSyncing = false
  
  // Injected dependencies (with defaults for production)
  private readonly httpClient: HttpClient
  private readonly realtimeService: typeof realtimeService
  private readonly leaderElection: typeof leaderElection
  private readonly connectionStateService: ConnectionStateService
  private readonly tabSyncCoordinator: TabSyncCoordinator
  private readonly memoryStateService: typeof memoryStateService
  private readonly hydrationCoordinator?: HydrationCoordinatorLike
  private syncListeners: Set<() => void> = new Set()
  private discardListeners: Set<(item: SyncQueueItem, reason: string) => void> = new Set()
  private pendingCountListeners: Set<(count: number) => void> = new Set()
  private syncCompleteCallbacks: Set<() => void> = new Set()
  private userGroupSyncListeners: Set<(isInProgress: boolean) => void> = new Set()
  
  private displayedPendingCount = 0
  private pendingCountTimeout: number | null = null
  private consecutiveFailures = 0 // Track consecutive command failures
  private backoffTimeout: number | null = null // Track backoff delay to cancel if needed
  private backoffResolve: (() => void) | null = null // Resolve function to cancel backoff
  // private logCleanupScheduled = false
  
  // Event queue management for sync coordination
  private eventQueue: EventPayload[] = []
  private userGroupSyncInProgress = false
  private scenarioSyncInProgress = new Set<string>()
  private scenarioSyncRequests: Map<string, Promise<void>> = new Map()

  private backgroundSyncInterval: number | null = null
  private cleanupStatusListener: (() => void) | null = null
  private remoteUpdateUnsubscribe: (() => void) | null = null

  // Network quality tracking for adaptive hydration
  private lastResponseTimes: number[] = [] // Last N response times
  private static MAX_RESPONSE_TIME_SAMPLES = 5
  private static SLOW_NETWORK_THRESHOLD = 2000 // 2 seconds
  
  // Batch processing configuration
  private static BATCH_SIZE = 10 // Max commands per batch
  private static MIN_BATCH_SIZE = 2 // Minimum commands to trigger batch (else single)

  constructor(
    deps?: {
      httpClient?: HttpClient
      realtimeService?: typeof realtimeService
      leaderElection?: typeof leaderElection
      connectionStateService?: ConnectionStateService
      tabSyncCoordinator?: TabSyncCoordinator
      memoryStateService?: typeof memoryStateService
      hydrationCoordinator?: HydrationCoordinatorLike
    }
  ) {
    // Use provided dependencies or fall back to singletons
    this.httpClient = deps?.httpClient ?? httpClient
    this.realtimeService = deps?.realtimeService ?? realtimeService
    this.leaderElection = deps?.leaderElection ?? leaderElection
    this.connectionStateService = deps?.connectionStateService ?? connectionStateService
    this.tabSyncCoordinator = deps?.tabSyncCoordinator ?? tabSyncCoordinator
    this.memoryStateService = deps?.memoryStateService ?? memoryStateService
    this.hydrationCoordinator = deps?.hydrationCoordinator
  }

  async initialize(): Promise<void> {
    // Listen for status changes from connection service
    this.cleanupStatusListener = this.connectionStateService.onStatusChange(this.handleStatusChange)
    
    // Wire up snapshot required handler from coordinator
    this.tabSyncCoordinator.onSnapshotRequired(async (scenarioId) => {
      await this.applyScenarioSnapshot(scenarioId)
    })

    // Listen to remote updates via coordinator (instead of direct broadcast channel)
    this.remoteUpdateUnsubscribe = this.tabSyncCoordinator.onRemoteUpdate(() => {
      // Only the leader needs to react to other tabs' commands by pushing them to server
      if (this.leaderElection.isLeaderTab()) {
        logger.debug('[ServerSync] Remote update detected via coordinator, triggering sync')
        this.queueChanged()
        void this.sync()
      }
    })

    // Wire up realtime lifecycle event listeners
    // These handle sync orchestration when SignalR connects/reconnects
    // Note: onConnected/onReconnected do NOT trigger user group sync.
    // That happens via onUserGroupJoined, which fires after the group is actually joined.
    this.realtimeService.onConnected(() => {
      logger.debug('[ServerSync] SignalR connected')
      this.consecutiveFailures = 0
      this.connectionStateService.markApiHealthy()
    })

    this.realtimeService.onReconnected(() => {
      logger.debug('[ServerSync] SignalR reconnected')
      this.consecutiveFailures = 0
      this.connectionStateService.markApiHealthy()
    })

    this.realtimeService.onConnectionFailed(() => {
      logger.warn('[ServerSync] SignalR connection failed, marking API as down')
      this.connectionStateService.markApiDown()
    })

    this.realtimeService.onScenarioGroupJoined((scenarioId) => {
      logger.debug('[ServerSync] Scenario group joined, syncing scenario:', scenarioId)
      // When a scenario group is joined, sync that scenario (fire-and-forget)
      this.handleScenarioGroupConnected(scenarioId).catch(err => {
        logger.error('[ServerSync] Error in scenario group connected handler:', err)
      })
    })

    this.realtimeService.onUserGroupJoined(() => {
      logger.debug('[ServerSync] User group joined, syncing user group')
      // When user group is joined, sync user scenarios (fire-and-forget)
      this.handleUserGroupConnected().catch(err => {
        logger.error('[ServerSync] Error in user group joined handler:', err)
      })
    })

    // Start background sync
    this.startBackgroundSync()
    
    logger.debug('[ServerSync] Initialized')
  }

  cleanup(): void {
    if (this.backgroundSyncInterval) {
      clearInterval(this.backgroundSyncInterval)
      this.backgroundSyncInterval = null
    }

    if (this.cleanupStatusListener) {
      this.cleanupStatusListener()
      this.cleanupStatusListener = null
    }

    if (this.remoteUpdateUnsubscribe) {
      this.remoteUpdateUnsubscribe()
      this.remoteUpdateUnsubscribe = null
    }
    
    // Clear snapshot callback (optional, but good practice)
    this.tabSyncCoordinator.onSnapshotRequired(() => Promise.resolve()) // Reset to no-op or handle properly
  }

  /**
   * Reset the service state (used when switching users)
   * Clears queues and pending operations
   */
  reset(): void {
    logger.info('[ServerSync] Resetting service state')
    
    // Clear queues
    this.eventQueue = []
    this.scenarioSyncInProgress.clear()
    this.userGroupSyncInProgress = false
    
    // Reset counters
    this.consecutiveFailures = 0
    this.displayedPendingCount = 0
    this.notifyPendingCountListeners(0) // Update UI to 0
    
    // Cancel backoff
    if (this.backoffTimeout) {
      clearTimeout(this.backoffTimeout)
      this.backoffTimeout = null
    }
    this.backoffResolve = null
  }

  private handleStatusChange = (status: SyncStatus) => {
    logger.debug(`[ServerSync] Status changed to: ${status}`)
    
    // Trigger sync when coming back online or healthy
    if (status === 'online') {
      logger.info('[ServerSync] API is back online, cancelling backoff and triggering immediate sync')
       // Cancel any pending backoff delay
       if (this.backoffTimeout) {
        clearTimeout(this.backoffTimeout)
        this.backoffTimeout = null
        // Resolve the backoff Promise so sync loop continues immediately
        if (this.backoffResolve) {
          this.backoffResolve()
          this.backoffResolve = null
        }
      }
      // If sync is not running, start it
      if (!this.isSyncing) {
        this.sync()
      }
    }
  }

  private startBackgroundSync() {
    // Run background sync every 60 seconds if online and not syncing
    if (this.backgroundSyncInterval) clearInterval(this.backgroundSyncInterval)
    this.backgroundSyncInterval = window.setInterval(async () => {
      if (this.connectionStateService.isNetworkOnline() && !this.isSyncing) {
        await this.sync()
      }
    }, 60000)
  }

  getSyncStatus(): SyncStatus {
    return this.connectionStateService.getSyncStatus()
  }

  onSyncStatusChange(listener: (status: SyncStatus) => void): () => void {
    // Delegate to connectionStateService
    return this.connectionStateService.onStatusChange(listener)
  }

  onSyncComplete(listener: () => void): () => void {
    this.syncListeners.add(listener)
    return () => this.syncListeners.delete(listener)
  }

  onSyncDiscard(listener: (item: SyncQueueItem, reason: string) => void): () => void {
    this.discardListeners.add(listener)
    return () => this.discardListeners.delete(listener)
  }

  onPendingCountChange(listener: (count: number) => void): () => void {
    this.pendingCountListeners.add(listener)
    // Immediately notify with current count
    listener(this.displayedPendingCount)
    return () => this.pendingCountListeners.delete(listener)
  }
  
  onUserGroupSyncChange(listener: (isInProgress: boolean) => void): () => void {
    this.userGroupSyncListeners.add(listener)
    // Immediately notify with current state
    listener(this.userGroupSyncInProgress)
    return () => this.userGroupSyncListeners.delete(listener)
  }
  
  isUserGroupSyncInProgress(): boolean {
    return this.userGroupSyncInProgress
  }

  onSyncCompleteForCoordination(callback: () => void): () => void {
    this.syncCompleteCallbacks.add(callback)
    return () => this.syncCompleteCallbacks.delete(callback)
  }

  private notifySyncListeners() {
    this.syncListeners.forEach(listener => listener())
  }

  private notifyDiscardListeners(item: SyncQueueItem, reason: string) {
    this.discardListeners.forEach(listener => listener(item, reason))
  }

  private notifyPendingCountListeners(count: number) {
    this.pendingCountListeners.forEach(listener => listener(count))
  }
  
  private notifyUserGroupSyncListeners(isInProgress: boolean) {
    this.userGroupSyncListeners.forEach(listener => {
      try {
        listener(isInProgress)
      } catch (error) {
        logger.error('[ServerSync] Error in user group sync listener:', error)
      }
    })
  }

  private async updateDisplayedPendingCount() {
    const actualCount = await this.getPendingCount()
    
    // Always clear any pending timeout
    if (this.pendingCountTimeout) {
      clearTimeout(this.pendingCountTimeout)
      this.pendingCountTimeout = null
    }
    
    // If count is 0, hide immediately
    if (actualCount === 0) {
      this.displayedPendingCount = 0
      this.notifyPendingCountListeners(0)
      return
    }
    
    // If indicator is already showing (count > 0), update immediately
    if (this.displayedPendingCount > 0) {
      this.displayedPendingCount = actualCount
      this.notifyPendingCountListeners(actualCount)
      return
    }
    
    // Only delay when first showing the indicator (going from 0 to something)
    this.pendingCountTimeout = window.setTimeout(() => {
      this.displayedPendingCount = actualCount
      this.notifyPendingCountListeners(actualCount)
      this.pendingCountTimeout = null
    }, 4000)
  }

  getDisplayedPendingCount(): number {
    return this.displayedPendingCount
  }

  // Call this whenever the queue changes (items added)
  // Note: sync() automatically updates the pending count, so explicit calls are optional
  queueChanged() {
    this.updateDisplayedPendingCount()
  }

  /**
   * Wake the sync loop after a user action that unblocks the queue (e.g. discarding a dead-lettered command).
   * Cancels any in-flight backoff delay and triggers an immediate sync when possible.
   */
  requestImmediateSync() {
    // Keep UI pending indicator accurate
    void this.updateDisplayedPendingCount()

    // Cancel any pending backoff delay
    if (this.backoffTimeout) {
      clearTimeout(this.backoffTimeout)
      this.backoffTimeout = null
      if (this.backoffResolve) {
        this.backoffResolve()
        this.backoffResolve = null
      }
    }

    if (!this.isSyncing) {
      void this.sync()
    }
  }

  private classifyError(error: any): ErrorType {
    const apiError = error as ApiError
    
    // Check for timeout errors first (treat as network error for retry)
    if (isTimeoutError(error)) {
      return ErrorType.NetworkError
    }
    
    // Check HTTP status codes first
    if (apiError.status === 409) return ErrorType.Conflict
    if (apiError.status === 404) return ErrorType.NotFound
    if (apiError.status === 400) return ErrorType.BadRequest
    if (apiError.status === 403) return ErrorType.BadRequest // Forbidden = permanent failure
    if (apiError.status >= 500 && apiError.status < 600) return ErrorType.ServerError
    
    // Check error messages
    const message = error.message?.toLowerCase() || ''
    if (message.includes('concurrent update')) return ErrorType.Conflict
    if (message.includes('networkerror') || 
        message.includes('failed to fetch') ||
        message.includes('network request failed') ||
        error.name === 'NetworkError' ||
        error.name === 'AbortError') {
      return ErrorType.NetworkError
    }
    
    return ErrorType.Unknown
  }

  private shouldRetry(item: SyncQueueItem, errorType: ErrorType): boolean {
    // Network and server errors: retry indefinitely (API is temporarily down)
    if (errorType === ErrorType.ServerError || errorType === ErrorType.NetworkError) {
      return item.retries < UNLIMITED_RETRIES
    }
    
    // Conflicts: retry with limit (concurrent updates)
    if (errorType === ErrorType.Conflict) {
      return item.retries < MAX_CONFLICT_RETRIES
    }
    
    // Not Found: depends on operation type
    if (errorType === ErrorType.NotFound) {
      // For CREATE operations, 404 might mean it already exists - don't retry
      if (item.command.commandType.startsWith('Create')) return false
      // For UPDATE/DELETE, 404 means resource is gone - don't retry
      return false
    }
    
    // Bad Request: immediate failure (invalid data)
    if (errorType === ErrorType.BadRequest) return false
    
    // Unknown errors: moderate retry limit
    return item.retries < MAX_UNKNOWN_RETRIES
  }

  private getEntityName(item: SyncQueueItem): string {
    if (item.uiMetadata?.entityName) return item.uiMetadata.entityName;
    const cmd = item.command as any
    return cmd.name || cmd.itemName || cmd.listName || cmd.newName || item.commandId
  }

  private getEntityTypeFromCommand(commandType: string): 'scenario' | 'list' | 'item' | null {
    if (commandType.includes('Scenario')) return 'scenario'
    if (commandType.includes('List')) return 'list'
    if (commandType.includes('Item')) return 'item'
    return null
  }

  private getEntityIdFromCommand(command: any): string | null {
    return command.itemId || command.listId || command.scenarioId || null
  }

  private calculateBackoffDelay(retries: number): number {
    // Exponential backoff: delay = BASE * 2^retries, capped at MAX
    const delay = Math.min(BASE_RETRY_DELAY * Math.pow(2, retries), MAX_RETRY_DELAY)
    // Add jitter (±25%) to prevent thundering herd
    const jitter = delay * 0.25 * (Math.random() - 0.5)
    return Math.floor(delay + jitter)
  }

  async getPendingCount(): Promise<number> {
    const queue = await localDB.getSyncQueue() || []
    // Processed items are still in DB waiting for event, but should not count towards "Pending Uploads" UI
    return queue.filter(item => item.status !== 'processed').length
  }

  async getPendingItems(): Promise<SyncQueueItem[]> {
    const queue = await localDB.getSyncQueue()
    // Exclude 'processed' items — they've been accepted by the server and are
    // only kept temporarily until the corresponding event arrives to remove them.
    // Showing them as "Pending" in the UI would be misleading (the server has the data).
    return queue.filter(item => item.status !== 'processed')
  }

  /**
   * Remove sync queue items stuck in 'processed' status.
   * 
   * After a command is sent to the API and acknowledged, the item is marked 'processed'.
   * Normally, the corresponding event (returned inline or via SignalR) removes it atomically
   * in applyEvent(). However, several scenarios can leave items stranded:
   *   - API returned 204 NoContent (no event body)
   *   - Event was deduplicated by EventProcessor
   *   - Event processing failed (sequence gap, transaction error)
   *   - SignalR disconnected before event was delivered
   * 
   * Since the server has already accepted these commands, the data is safe.
   * Removing them prevents ghost items from polluting the sync queue and UI.
   */
  private async cleanupProcessedItems(): Promise<void> {
    const queue = await localDB.getSyncQueue() || []
    const processedItems = queue.filter(i => i.status === 'processed')

    if (processedItems.length === 0) return

    for (const item of processedItems) {
      logger.debug(`[Sync] Cleaning up processed item ${item.commandId}`)
      await localDB.removeSyncQueueItem(item.commandId)
      const entityType = this.getEntityTypeFromCommand(item.command.commandType)
      const entityId = this.getEntityIdFromCommand(item.command)
      if (entityType && entityId) {
        this.realtimeService.clearPendingOperation(entityType, entityId)
      }
    }

    logger.info(`[Sync] Cleaned up ${processedItems.length} processed items from sync queue`)
    await this.updateDisplayedPendingCount()
  }

  /**
   * Force clear all pending items (useful for debugging stuck syncs)
   */
  async forceClearPendingItems(): Promise<void> {
    logger.warn('Force clearing all pending sync items')
    const queue = await localDB.getSyncQueue()
    for (const item of queue) {
      await localDB.removeSyncQueueItem(item.commandId)
      // Extract entity info from command for realtime service
      const entityType = this.getEntityTypeFromCommand(item.command.commandType)
      const entityId = this.getEntityIdFromCommand(item.command)
      if (entityType && entityId) {
        this.realtimeService.clearPendingOperation(entityType, entityId)
      }
    }
    await this.updateDisplayedPendingCount()
    logger.info('All pending items cleared')
  }

  async sync(): Promise<void> {
    if (!this.connectionStateService.isNetworkOnline()) return
    if (this.isSyncing) return
    
    // Only leader tab should sync commands to server
    if (!this.leaderElection.isLeaderTab()) {
      logger.debug('[Sync] Skipping sync - not leader tab')
      return
    }

    this.isSyncing = true
    
    // Update pending count at start of sync
    await this.updateDisplayedPendingCount()
    
    let consecutiveFailuresThisSync = 0
    const MAX_CONSECUTIVE_FAILURES = 3 // Stop after 3 consecutive failures (circuit breaker)
    
    // Keep syncing until queue is empty or circuit breaker trips
    while (true) {
      // Half-open circuit breaker: when API is marked down, probe before giving up
      if (!this.connectionStateService.isApiUp()) {
        const probeResult = await this.probeApiHealth()
        if (!probeResult) {
          logger.debug('[Sync] API probe failed, staying in circuit-open state')
          break
        }
        // Probe succeeded — API is back, continue with normal sync
        logger.info('[Sync] API probe succeeded, circuit breaker closed, resuming sync')
      }
      
      // PRESERVE FIFO: Check for blocked scenarios (those with Dead Letter entries)
      const blockedScenarioIds = await localDB.getBlockedScenarioIds()

      const queue = await localDB.getSyncQueue() || []
      
      // Safety: Reset any items stuck in 'processing' status back to 'pending'
      // This can happen if app crashed/closed while syncing
      for (const item of queue) {
        if (item.status === 'processing') {
          logger.warn(`[Sync] Resetting stuck item ${item.commandId} from 'processing' to 'pending'`)
          item.status = 'pending'
          await localDB.updateSyncQueueItem(item)
        }
      }
      
      // Skip items that are already processed (waiting for event)
      const pendingItems = queue.filter(i => {
        if (i.status === 'processed') return false
        
        // FIFO Protection: Skip items for scenarios that have errors in Dead Letter Queue
        // This ensures dependent commands don't run out of order
        if (blockedScenarioIds.has(i.command.scenarioId)) {
           return false
        }
        return true
      })
      
      if (pendingItems.length === 0) {
        logger.debug('Queue empty (or all processed/blocked), sync complete')
        break
      }
      
      // Check if we should use batch processing
      // Use batch when we have multiple items for the same scenario
      const batches = this.groupIntoBatches(pendingItems)
      const useBatch = batches.some(b => b.length >= ServerSyncService.MIN_BATCH_SIZE)
      
      if (useBatch) {
        // Process first batch that has enough items
        const batchToProcess = batches.find(b => b.length >= ServerSyncService.MIN_BATCH_SIZE) || batches[0]
        
        logger.debug(`[Sync] Using batch processing for ${batchToProcess.length} commands`)
        
        // Mark all operations as pending to prevent SignalR race condition
        for (const batchItem of batchToProcess) {
          const batchEntityType = this.getEntityTypeFromCommand(batchItem.command.commandType)
          const batchEntityId = this.getEntityIdFromCommand(batchItem.command)
          if (batchEntityType && batchEntityId) {
            this.realtimeService.markOperationPending(batchEntityType, batchEntityId)
          }
        }
        
        try {
          const batchResults = await this.processBatch(batchToProcess)
          
          // Process each result
          for (const batchItem of batchToProcess) {
            const batchResult = batchResults.get(batchItem.commandId)
            const batchEntityType = this.getEntityTypeFromCommand(batchItem.command.commandType)
            const batchEntityId = this.getEntityIdFromCommand(batchItem.command)
            
            if (batchResult?.result) {
              // Success - process event if returned
              if (batchResult.result.event) {
                const eventData = batchResult.result.event
                const eventPayload: EventPayload = {
                  eventType: eventData.eventType,
                  eventId: eventData.eventId,
                  scenarioId: eventData.scenarioId || eventData.sid,
                  sequenceNumber: eventData.sequenceNumber,
                  originId: eventData.originId,
                  commandId: eventData.commandId,
                  serverTimestamp: eventData.serverTimestamp,
                  clientTimestamp: eventData.clientTimestamp,
                  userId: eventData.userId,
                  payload: eventData.payload || {}
                }
                await this.tabSyncCoordinator.processEvent(eventPayload)
              }
              
              consecutiveFailuresThisSync = 0
              this.consecutiveFailures = 0
              this.connectionStateService.markApiHealthy()
              
              if (batchEntityType && batchEntityId) {
                this.realtimeService.clearPendingOperation(batchEntityType, batchEntityId)
              }
              
              logger.debug(`[Sync] ✓ Batch success: ${batchEntityType}/${batchEntityId}`)
              
              // Log successful sync asynchronously (don't wait)
              // Try to get friendly name for log entry
              let batchFriendlyName = this.getEntityName(batchItem)
              // If the name depends on lookup (item update)
              if (batchEntityType === 'item' && batchEntityId && batchFriendlyName === batchEntityId) {
                try {
                  const savedItem = await localDB.getListItem(batchEntityId)
                  if (savedItem) batchFriendlyName = savedItem.name
                } catch {}
              } else if (batchEntityType === 'list' && batchEntityId && batchFriendlyName === batchEntityId) {
                try {
                  const savedList = await localDB.getList(batchEntityId)
                  if (savedList) batchFriendlyName = savedList.name
                } catch {}
              }

              localDB.addSyncLogEntry({
                timestamp: Date.now(),
                type: batchItem.command.commandType.startsWith('Create') ? 'create' : 
                      batchItem.command.commandType.startsWith('Delete') ? 'delete' : 'update',
                entity: batchEntityType as any,
                entityId: batchEntityId!,
                entityName: batchFriendlyName,
                actionDescription: batchItem.uiMetadata?.description,
                status: 'success',
                retries: batchItem.retries
              }).catch(err => logger.warn('Failed to log batch sync success:', err))
            } else if (batchResult?.error) {
              // Error - handle similar to single item
              if (batchEntityType && batchEntityId) {
                this.realtimeService.clearPendingOperation(batchEntityType, batchEntityId)
              }
              
              consecutiveFailuresThisSync++
              
              // Track consecutive failures for API health (server/network errors)
              const batchErrorType = this.classifyError(batchResult.error)
              if (batchErrorType === ErrorType.ServerError || batchErrorType === ErrorType.NetworkError) {
                this.consecutiveFailures++
                if (this.consecutiveFailures >= 3) {
                  logger.warn('[ServerSync] API marked unhealthy after 3 consecutive batch failures')
                  this.connectionStateService.markApiDown()
                }
              }
              
              logger.warn(`[Sync] Batch item failed: ${batchItem.commandId}`, batchResult.error)

              // Apply the same permanent-failure behavior as single-item processing.
              // This prevents infinite retry loops for permanently invalid commands (e.g., unknown command types).
              const batchErrorMessage = batchResult.error instanceof Error
                ? batchResult.error.message
                : String(batchResult.error)

              batchItem.retries++
              batchItem.lastError = batchErrorMessage
              batchItem.status = 'pending'

              if (this.shouldRetry(batchItem, batchErrorType)) {
                await localDB.updateSyncQueueItem(batchItem)
              } else {
                // Permanent failure - move to dead letter queue
                const entityName = this.getEntityName(batchItem)
                const action = batchItem.command.commandType.startsWith('Create') ? 'Create' :
                              batchItem.command.commandType.startsWith('Delete') ? 'Delete' : 'Update'
                const entityTypeName = (batchEntityType ? batchEntityType.charAt(0).toUpperCase() + batchEntityType.slice(1) : 'Entity')

                let reason: string
                let canRetry: boolean = true
                let errorCode: 'ACCESS_DENIED' | 'NOT_FOUND' | 'CONFLICT' | 'VALIDATION' | 'NETWORK' | 'UNKNOWN' = 'UNKNOWN'

                if (batchErrorType === ErrorType.Conflict) {
                  reason = `${action} ${entityTypeName} "${entityName}": Failed after ${batchItem.retries} attempts due to concurrent updates. Please refresh and try again.`
                  canRetry = true
                  errorCode = 'CONFLICT'
                } else if (batchErrorType === ErrorType.NotFound) {
                  reason = `${action} ${entityTypeName} "${entityName}": Resource not found on server. It may have been deleted.`
                  canRetry = false
                  errorCode = 'NOT_FOUND'
                } else if (batchErrorType === ErrorType.BadRequest) {
                  if (batchErrorMessage.includes('403') || batchErrorMessage.includes('Forbidden')) {
                    reason = `${action} ${entityTypeName} "${entityName}": Access denied. This could be a permission issue or a temporary network/firewall problem. You can retry or discard.`
                    canRetry = true
                    errorCode = 'ACCESS_DENIED'
                  } else {
                    reason = `${action} ${entityTypeName} "${entityName}": Invalid data. ${batchErrorMessage}`
                    canRetry = false
                    errorCode = 'VALIDATION'
                  }
                } else if (batchErrorType === ErrorType.NetworkError) {
                  reason = `${action} ${entityTypeName} "${entityName}": Network error. ${batchErrorMessage}`
                  canRetry = true
                  errorCode = 'NETWORK'
                } else {
                  reason = `${action} ${entityTypeName} "${entityName}": Failed after ${batchItem.retries} attempts. ${batchErrorMessage}`
                  canRetry = true
                  errorCode = 'UNKNOWN'
                }

                try {
                  await localDB.moveToDeadLetterQueue(batchItem, reason, canRetry, errorCode)
                  this.notifyDiscardListeners(batchItem, reason)
                } catch (err) {
                  logger.error('[Sync] Failed to move batch item to dead letter queue:', err)
                  // If atomic move fails, item stays in sync queue and will retry next sync pass
                  await localDB.updateSyncQueueItem(batchItem)
                }
              }
            } else {
              // No result at all - command was not processed (server stopped on earlier failure)
              // Reset to pending so it will be retried on next sync pass
              if (batchEntityType && batchEntityId) {
                this.realtimeService.clearPendingOperation(batchEntityType, batchEntityId)
              }
              
              logger.debug(`[Sync] Batch item not processed (FIFO stop): ${batchItem.commandId}`)
              batchItem.status = 'pending'
              await localDB.updateSyncQueueItem(batchItem)
            }
          }
          
          await this.updateDisplayedPendingCount()
          
          // Circuit breaker: if too many consecutive failures, apply backoff and exit
          if (consecutiveFailuresThisSync >= MAX_CONSECUTIVE_FAILURES) {
            const backoffDelay = this.calculateBackoffDelay(this.consecutiveFailures)
            logger.debug(`[Sync] Circuit breaker (batch): ${consecutiveFailuresThisSync} consecutive failures, backing off ${Math.round(backoffDelay/1000)}s`)
            
            await new Promise<void>(resolve => {
              this.backoffResolve = resolve
              this.backoffTimeout = window.setTimeout(() => {
                this.backoffTimeout = null
                this.backoffResolve = null
                resolve()
              }, backoffDelay)
            })
            
            // Reset counter after backoff, but check if API is still down
            consecutiveFailuresThisSync = 0
            if (!this.connectionStateService.isApiUp()) {
              logger.debug('[Sync] API still down after backoff, stopping sync')
              break
            }
            logger.debug('Backoff complete, retrying...')
          }
          
          // Continue to next iteration
          continue
          
        } catch (batchError) {
          // Batch-level error - clear all pending and continue with single-item processing
          for (const batchItem of batchToProcess) {
            const batchEntityType = this.getEntityTypeFromCommand(batchItem.command.commandType)
            const batchEntityId = this.getEntityIdFromCommand(batchItem.command)
            if (batchEntityType && batchEntityId) {
              this.realtimeService.clearPendingOperation(batchEntityType, batchEntityId)
            }
          }
          logger.warn('[Sync] Batch processing failed, falling back to single-item mode:', batchError)
          // Fall through to single-item processing
        }
      }
      
      // Single item processing (original logic)
      // Take first item from queue (FIFO)
      const item = pendingItems[0]
      const entityType = this.getEntityTypeFromCommand(item.command.commandType)
      const entityId = this.getEntityIdFromCommand(item.command)
      logger.debug(`[Sync] Processing ${item.command.commandType} (${queue.length} in queue)`)
      
      try {
        // Mark operation as pending to prevent SignalR race condition
        if (entityType && entityId) {
          this.realtimeService.markOperationPending(entityType, entityId)
        }
        
        const result = await this.processSyncItem(item)
        
        // After success, we mark item as processed but DO NOT remove it.
        // It will be removed atomically when the corresponding event arrives.
        // If the API returned the event immediately, we process it now.
        // If not, it stays in the queue until the SignalR event arrives to remove it.
        await localDB.updateCommandStatus(item.commandId, 'processed')

        // If API returned the event (Offline First pattern), process it immediately
        if (result && result.event) {
          logger.debug(`[Sync] Processing returned event for command ${item.commandId}`)
          const eventData = result.event
          // Map to EventPayload
          const eventPayload: EventPayload = {
            eventType: eventData.eventType,
            eventId: eventData.eventId,
            scenarioId: eventData.scenarioId || eventData.sid,
            sequenceNumber: eventData.sequenceNumber,
            originId: eventData.originId,
            commandId: eventData.commandId,
            serverTimestamp: eventData.serverTimestamp,
            clientTimestamp: eventData.clientTimestamp,
            userId: eventData.userId,
            payload: eventData.payload || {}
          }
          // Use coordinator to process event, reload memory, and broadcast to other tabs
          await this.tabSyncCoordinator.processEvent(eventPayload)
        }
        
        await this.updateDisplayedPendingCount()
        
        // Reset failure counters on success
        consecutiveFailuresThisSync = 0
        this.consecutiveFailures = 0
        this.connectionStateService.markApiHealthy() // Report success to connection service
        
        // Clear pending after successful sync
        if (entityType && entityId) {
          this.realtimeService.clearPendingOperation(entityType, entityId)
        }
        logger.debug(`[Sync] ✓ Success: ${entityType}/${entityId}`)
        
        // Log successful sync asynchronously (don't wait)
        
        // Try to get friendly name for log entry
        let friendlyName = this.getEntityName(item)
        // If the name depends on lookup (item update)
        if (entityType === 'item' && entityId && friendlyName === entityId) { // Fallback ID usage in getEntityName
           try {
             const savedItem = await localDB.getListItem(entityId)
             if (savedItem) friendlyName = savedItem.name
           } catch {}
        }
        else if (entityType === 'list' && entityId && friendlyName === entityId) {
            try {
              const savedList = await localDB.getList(entityId)
              if (savedList) friendlyName = savedList.name
            } catch {}
        }

        localDB.addSyncLogEntry({
          timestamp: Date.now(),
          type: item.command.commandType.startsWith('Create') ? 'create' : 
                item.command.commandType.startsWith('Delete') ? 'delete' : 'update',
          entity: entityType as any,
          entityId: entityId!,
          entityName: friendlyName,
          actionDescription: item.uiMetadata?.description,
          status: 'success',
          retries: item.retries
        }).catch(err => logger.warn('Failed to log sync success:', err))
        
        // Continue immediately to next item
        continue
        
      } catch (error) {
        // Clear pending on error
        if (entityType && entityId) {
          this.realtimeService.clearPendingOperation(entityType, entityId)
        }
        
        consecutiveFailuresThisSync++
        
        const errorType = this.classifyError(error)
        const errorMessage = error instanceof Error ? error.message : String(error)
        
        logger.error(`[Sync] ✗ Failed: ${entityType}/${entityId}`, error)
        
        // Track consecutive failures for API health
        if (errorType === ErrorType.ServerError || errorType === ErrorType.NetworkError) {
          this.consecutiveFailures++
          if (this.consecutiveFailures >= 3) {
            logger.warn('[ServerSync] API marked unhealthy after 3 consecutive failures')
            this.connectionStateService.markApiDown() // Report failure to connection service
          }
        } else {
          this.consecutiveFailures = 0
        }
        
        // Update item with error info
        item.retries++
        item.lastError = errorMessage
        
        // Reset status back to 'pending' so it can be retried
        // (it might be stuck in 'processing' if error occurred during processSyncItem)
        item.status = 'pending'
        
        if (this.shouldRetry(item, errorType)) {
          // Keep in queue for retry
          await localDB.updateSyncQueueItem(item)
          const maxRetries = errorType === ErrorType.Conflict ? MAX_CONFLICT_RETRIES : 
                            errorType === ErrorType.NetworkError || errorType === ErrorType.ServerError ? UNLIMITED_RETRIES :
                            MAX_UNKNOWN_RETRIES
          logger.debug(`Will retry (${item.retries}/${maxRetries}):`, item.commandId)
          
          // Circuit breaker: if too many consecutive failures, stop and wait
          if (consecutiveFailuresThisSync >= MAX_CONSECUTIVE_FAILURES) {
            const backoffDelay = this.calculateBackoffDelay(item.retries)
            logger.debug(`[Sync] Circuit breaker: ${consecutiveFailuresThisSync} consecutive failures, backing off ${Math.round(backoffDelay/1000)}s`)
            
            await new Promise<void>(resolve => {
              this.backoffResolve = resolve
              this.backoffTimeout = window.setTimeout(() => {
                this.backoffTimeout = null
                this.backoffResolve = null
                resolve()
              }, backoffDelay)
            })
            
            // Reset counter after backoff, but check if API is still down
            consecutiveFailuresThisSync = 0
            if (!this.connectionStateService.isApiUp()) {
              logger.debug('[Sync] API still down after backoff, stopping sync')
              break
            }
            logger.debug('Backoff complete, retrying...')
          }
        } else {
            // Max retries reached or permanent failure - move to dead letter queue
            const entityType = this.getEntityTypeFromCommand(item.command.commandType)
            const entityId = this.getEntityIdFromCommand(item.command)
            logger.info(`[Sync] Permanent failure detected for ${entityId}, moving to dead letter queue`)
            
            // Build user-friendly failure reason
            let reason: string
            let canRetry: boolean = true // Default: allow user to retry
            let errorCode: 'ACCESS_DENIED' | 'NOT_FOUND' | 'CONFLICT' | 'VALIDATION' | 'NETWORK' | 'UNKNOWN' = 'UNKNOWN'
            const entityName = this.getEntityName(item)
            const action = item.command.commandType.startsWith('Create') ? 'Create' :
                          item.command.commandType.startsWith('Delete') ? 'Delete' : 'Update'
            const entityTypeName = (entityType ? entityType.charAt(0).toUpperCase() + entityType.slice(1) : 'Entity')
            
            if (errorType === ErrorType.Conflict) {
              reason = `${action} ${entityTypeName} "${entityName}": Failed after ${item.retries} attempts due to concurrent updates. Please refresh and try again.`
              canRetry = true // User can retry after refresh
              errorCode = 'CONFLICT'
            } else if (errorType === ErrorType.NotFound) {
              reason = `${action} ${entityTypeName} "${entityName}": Resource not found on server. It may have been deleted.`
              canRetry = false // No point retrying if resource is gone
              errorCode = 'NOT_FOUND'
            } else if (errorType === ErrorType.BadRequest) {
              // Check if it's a 403 Forbidden
              if (errorMessage.includes('403') || errorMessage.includes('Forbidden')) {
                // 403 could be a firewall issue, not actual permission denial
                // Always allow retry and let user decide
                reason = `${action} ${entityTypeName} "${entityName}": Access denied. This could be a permission issue or a temporary network/firewall problem. You can retry or discard.`
                canRetry = true // 403 could be firewall - allow retry
                errorCode = 'ACCESS_DENIED'
              } else {
                reason = `${action} ${entityTypeName} "${entityName}": Invalid data. ${errorMessage}`
                canRetry = false // Invalid data won't be fixed by retrying
                errorCode = 'VALIDATION'
              }
            } else if (errorType === ErrorType.NetworkError) {
              reason = `${action} ${entityTypeName} "${entityName}": Network error. ${errorMessage}`
              canRetry = true
              errorCode = 'NETWORK'
            } else {
              reason = `${action} ${entityTypeName} "${entityName}": Failed after ${item.retries} attempts. ${errorMessage}`
              canRetry = true // Unknown errors might resolve
              errorCode = 'UNKNOWN'
            }
            
            // ATOMIC: Move to dead letter queue (removes from sync queue + adds to dead letter in one transaction)
            try {
              await localDB.moveToDeadLetterQueue(item, reason, canRetry, errorCode)
              logger.debug(`[Sync] Moved ${item.commandId} to dead letter queue:`, reason)
            } catch (err) {
              logger.error(`[Sync] Failed to move to dead letter queue:`, err)
              // If atomic move fails, item stays in sync queue and will retry next sync pass
            }
            
            // Update displayed count after removing item
            try {
              await this.updateDisplayedPendingCount()
              logger.debug(`[Sync] Updated pending count after discard`)
            } catch (err) {
              logger.error(`[Sync] Failed to update pending count:`, err)
            }
            
            // Don't log to syncLog yet - item is in limbo waiting for user action
            // Will be logged when user manually discards from Failed Operations
            
            logger.debug(`[Sync] Moved to dead letter queue:`, entityId, reason)
            this.notifyDiscardListeners(item, reason)
        }
      }
    }

    // Clean up stale 'processed' items — these commands were accepted by the server
    // but their events may have been lost, deduped, or arrived via a path that didn't
    // remove them from the queue (e.g., 204 NoContent, sequence gap). The server has
    // the data, so it's safe to remove them.
    await this.cleanupProcessedItems()

    // Notify listeners once after all passes complete
    this.notifySyncListeners()
    this.updateDisplayedPendingCount()
    
    // Notify coordination callbacks (e.g., realtimeService)
    this.syncCompleteCallbacks.forEach(callback => callback())
    
    this.isSyncing = false
  }

  // ==================== Half-Open Circuit Breaker ====================

  /**
   * Probe API health when circuit breaker is open (API marked down).
   * 
   * Two strategies:
   * 1. Queue has items → try sending the first actual command (the command IS the probe)
   * 2. Queue is empty → try SignalR reconnect (lightweight connectivity check)
   * 
   * Returns true if API is confirmed healthy, false if still down.
   */
  private async probeApiHealth(): Promise<boolean> {
    const queue = await localDB.getSyncQueue() || []
    const pendingItems = queue.filter(i => i.status !== 'processed')
    
    if (pendingItems.length > 0) {
      // Strategy 1: Use actual command as probe
      return await this.probeWithCommand(pendingItems[0])
    } else {
      // Strategy 2: Try SignalR reconnect as lightweight probe
      return await this.probeWithSignalR()
    }
  }

  /**
   * Probe by sending the first queued command to the API.
   * If it succeeds, the API is back — mark healthy and let normal sync continue.
   * If it fails with a server/network error, stay in circuit-open state.
   */
  private async probeWithCommand(item: SyncQueueItem): Promise<boolean> {
    logger.debug(`[Sync] Half-open probe: attempting command ${item.commandId}`)
    
    const entityType = this.getEntityTypeFromCommand(item.command.commandType)
    const entityId = this.getEntityIdFromCommand(item.command)
    
    try {
      if (entityType && entityId) {
        this.realtimeService.markOperationPending(entityType, entityId)
      }
      
      const result = await this.processSyncItem(item)
      
      // Success — API is back!
      await localDB.updateCommandStatus(item.commandId, 'processed')
      
      if (result && result.event) {
        const eventData = result.event
        const eventPayload: EventPayload = {
          eventType: eventData.eventType,
          eventId: eventData.eventId,
          scenarioId: eventData.scenarioId || eventData.sid,
          sequenceNumber: eventData.sequenceNumber,
          originId: eventData.originId,
          commandId: eventData.commandId,
          serverTimestamp: eventData.serverTimestamp,
          clientTimestamp: eventData.clientTimestamp,
          userId: eventData.userId,
          payload: eventData.payload || {}
        }
        await this.tabSyncCoordinator.processEvent(eventPayload)
      }
      
      if (entityType && entityId) {
        this.realtimeService.clearPendingOperation(entityType, entityId)
      }
      
      // Reset failure counters and mark healthy
      this.consecutiveFailures = 0
      this.connectionStateService.markApiHealthy()
      
      await this.updateDisplayedPendingCount()
      logger.info(`[Sync] Half-open probe succeeded, API is back online`)
      return true
      
    } catch (error) {
      if (entityType && entityId) {
        this.realtimeService.clearPendingOperation(entityType, entityId)
      }
      
      const errorType = this.classifyError(error)
      
      // Server/network errors → API is still down
      if (errorType === ErrorType.ServerError || errorType === ErrorType.NetworkError) {
        logger.debug(`[Sync] Half-open probe failed (${errorType}), API still down`)
        // Update retry count but keep in queue
        item.retries++
        item.lastError = error instanceof Error ? error.message : String(error)
        item.status = 'pending'
        await localDB.updateSyncQueueItem(item)
        return false
      }
      
      // Non-transient error (400, 403, 404, 409) → API is reachable, just this command failed
      // Mark API healthy and let normal sync handle the error
      this.consecutiveFailures = 0
      this.connectionStateService.markApiHealthy()
      logger.info(`[Sync] Half-open probe got non-transient error (${errorType}), API is reachable`)
      return true
    }
  }

  /**
   * Probe by attempting SignalR reconnection.
   * This is used when the queue is empty but we need to check if the API is back.
   */
  private async probeWithSignalR(): Promise<boolean> {
    logger.debug('[Sync] Half-open probe: attempting SignalR reconnect (empty queue)')
    
    try {
      const connected = await this.realtimeService.tryReconnect()
      if (connected) {
        this.consecutiveFailures = 0
        this.connectionStateService.markApiHealthy()
        logger.info('[Sync] Half-open SignalR probe succeeded, API is back online')
        return true
      }
      
      logger.debug('[Sync] Half-open SignalR probe failed, API still down')
      return false
    } catch (error) {
      logger.debug('[Sync] Half-open SignalR probe threw:', error)
      return false
    }
  }

  private async processSyncItem(item: SyncQueueItem): Promise<CommandResponse | null> {
    // All queue items now contain Command objects
    if (item.command) {
      return await this.processCommand(item)
    }

    // If we get here, it's an old queue item type that should have been migrated
    console.warn('[Sync] Encountered queue item without command:', item)
    throw new Error(`Invalid queue item: missing command object`)
  }



  /**
   * Process any command generically - serverSyncService doesn't need to know about entity types
   */
  private async processCommand(item: SyncQueueItem): Promise<CommandResponse | null> {
    const { command, commandId } = item
    
    // Validate required command metadata
    if (!command || !commandId) {
      console.error('[Sync] Queue item missing required command')
      throw new Error('Queue item missing required command')
    }

    logger.debug(`[Sync] Processing command ${command.commandType} (${commandId})`)

    try {
      // Mark command as processing
      await localDB.updateCommandStatus(commandId, 'processing')
      
      // Send command directly to API without re-executing through commandExecutor
      // (command was already applied optimistically and broadcast when first queued)
      const response = await this.httpClient.post<any>('/api/commands', command)
      
      // Determine result type
      let result: CommandResponse
      
      if (!response.data) {
        // Handle NoContent response (204) - server succeeded but returned no body
        result = {
          status: 'success',
          commandId: command.commandId
        }
      } else if (response.data.status) {
        // Standard CommandResponse format
        result = response.data
      } else if (response.data.eventType || response.data.eventId) {
        // Raw Event object (API returns this on success/duplicate for Offline First)
        result = {
          status: 'success',
          commandId: response.data.commandId || command.commandId,
          eventId: response.data.eventId,
          event: response.data
        }
        logger.debug(`[Sync] API returned raw event for ${command.commandId}, mapped to success`)
      } else {
        // Unknown format
        result = response.data
      }
      
      // Validate result
      if (!result || !result.status) {
        console.error('[Sync] Invalid command result:', result)
        throw new Error('Command execution returned invalid result')
      }

      // Mark command as processed (will be removed when event arrives)
      await localDB.updateCommandStatus(commandId, 'processed')

      // Handle result - same logic for all command types
      if (result.status === 'duplicate') {
        // Duplicate command, treating as success
        return result // Already processed, treat as success
      }  
      
      if (result.status === 'error') {
        // If error is "not found" for delete operations, treat as success
        const isDeleteCommand = command.commandType.startsWith('Delete')
        if (isDeleteCommand && result.error?.toLowerCase().includes('not found')) {
          // Delete target not found, treating as success
          return null
        }
        
        // Create ApiError with status code extracted from error message
        const errorMessage = result.error || 'Command execution failed'
        const apiError = new Error(errorMessage) as ApiError
        
        // Extract status code from error message (e.g., "API request failed: 403" or "(HTTP 403)")
        const statusMatch = errorMessage.match(/(?:HTTP\s+|:\s*)(\d{3})/)
        if (statusMatch) {
          apiError.status = parseInt(statusMatch[1], 10)
        }
        
        throw apiError
      }
      
      // Command executed successfully
      return result
      
    } catch (error) {
      throw error
    }
  }

  /**
   * Process multiple commands in a batch for improved network efficiency.
   * Groups commands by scenario and sends to batch API endpoint.
   */
  private async processBatch(items: SyncQueueItem[]): Promise<Map<string, { result: CommandResponse | null, error?: Error }>> {
    const results = new Map<string, { result: CommandResponse | null, error?: Error }>()
    
    if (items.length === 0) return results
    
    logger.debug(`[Sync] Processing batch of ${items.length} commands`)
    
    // Mark all items as processing
    for (const item of items) {
      await localDB.updateCommandStatus(item.commandId, 'processing')
    }
    
    try {
      // Send batch to API
      const commands = items.map(item => item.command)
      const response = await this.httpClient.executeCommandBatch(commands)
      
      // Process results
      for (let i = 0; i < response.results.length; i++) {
        const batchResult = response.results[i]
        
        if (batchResult.success) {
          // Mark as processed
          await localDB.updateCommandStatus(batchResult.commandId, 'processed')
          
          // Build CommandResponse
          const result: CommandResponse = {
            status: 'success',
            commandId: batchResult.commandId,
            event: batchResult.event
          }
          results.set(batchResult.commandId, { result })
        } else {
          // Create error
          const error = new Error(batchResult.error || 'Command failed') as ApiError
          error.status = batchResult.statusCode || 500
          error.statusText = batchResult.statusCode?.toString() || 'Error'
          
          results.set(batchResult.commandId, { result: null, error })
        }
      }
      
      // Reset any commands that weren't in the response back to 'pending'
      // This happens when server stops processing on first failure (FIFO)
      for (const item of items) {
        if (!results.has(item.commandId)) {
          await localDB.updateCommandStatus(item.commandId, 'pending')
        }
      }
      
      return results
    } catch (error) {
      // Network/batch error - mark all as failed
      for (const item of items) {
        results.set(item.commandId, { result: null, error: error as Error })
      }
      return results
    }
  }

  /**
   * Group pending items into batches by scenario for efficient processing.
   * Returns batches where each batch contains commands for the same scenario.
   */
  private groupIntoBatches(items: SyncQueueItem[]): SyncQueueItem[][] {
    const byScenario = new Map<string, SyncQueueItem[]>()
    
    for (const item of items) {
      const scenarioId = item.command.scenarioId
      const existing = byScenario.get(scenarioId) || []
      existing.push(item)
      byScenario.set(scenarioId, existing)
    }
    
    const batches: SyncQueueItem[][] = []
    for (const scenarioItems of byScenario.values()) {
      // Split into chunks of BATCH_SIZE
      for (let i = 0; i < scenarioItems.length; i += ServerSyncService.BATCH_SIZE) {
        batches.push(scenarioItems.slice(i, i + ServerSyncService.BATCH_SIZE))
      }
    }
    
    return batches
  }

  // ==================== Snapshot Recovery ====================

  /**
   * Fetch and apply complete scenario snapshot (for gap recovery when events expired)
   */
  async applyScenarioSnapshot(scenarioId: string): Promise<void> {
    // Prevent concurrent snapshot apply for the same scenario, and ensure events
    // arriving during the snapshot transaction are queued.
    if (this.scenarioSyncInProgress.has(scenarioId)) {
      logger.debug(`[SnapshotRecovery] Snapshot apply already in progress for ${scenarioId}, skipping duplicate request`)
      return
    }

    this.scenarioSyncInProgress.add(scenarioId)

    try {
      logger.debug(`[SnapshotRecovery] Fetching snapshot for scenario ${scenarioId}`)

      // Force snapshot bootstrap/recovery (API guarantees snapshot when since < 0)
      const syncResponse = await this.httpClient.syncScenario(scenarioId, -1)
      
      if (!syncResponse.snapshot) {
        logger.warn(`[SnapshotRecovery] No snapshot returned from sync endpoint for ${scenarioId}`)
        return
      }
      
      logger.info(`[SnapshotRecovery] Applying snapshot for scenario ${scenarioId} with sequence ${syncResponse.snapshot.latestSequenceNumber}`)
      
      // Determine user role for this scenario
      let userRole: 'owner' | 'editor' = 'editor'
      try {
        const userProfile = await this.httpClient.getUserProfile()
        const access = (userProfile.scenarioAccesses || []).find((a: any) => a.scenarioId === scenarioId)
        const roleCandidate = String(access?.role || '').toLowerCase()
        userRole = roleCandidate === 'owner' ? 'owner' : 'editor'
      } catch (err) {
        // Ignore and fall back to safest default (editor)
      }
      
      // Apply snapshot atomically with recent history events
      await localDB.applyScenarioSnapshotAtomic(
        scenarioId,
        syncResponse.snapshot,
        syncResponse.events,
        userRole
      )
      
      logger.info(`[SnapshotRecovery] Snapshot applied successfully for scenario ${scenarioId}`)
      
      // Notify coordinator to reload memory and broadcast
      await this.tabSyncCoordinator.applySnapshot(scenarioId)
      
      // Notify listeners that data has changed
      this.notifySyncListeners()
      
    } catch (error) {
      console.error(`[SnapshotRecovery] Failed to apply snapshot for scenario ${scenarioId}:`, error)
      throw error
    } finally {
      try {
        await this.processQueuedEvents()
      } finally {
        this.scenarioSyncInProgress.delete(scenarioId)
      }
    }
  }



  private userGroupSyncInFlight: Promise<void> | null = null

  /**
   * Handler: User group subscription established - sync user profile and scenarios
   * Returns immediately while sync continues in background (non-blocking)
   * Deduplicates concurrent calls - if sync is already in-flight, the second call is a no-op.
   */
  async handleUserGroupConnected(): Promise<void> {
    // Deduplicate: if a sync is already in-flight, skip
    if (this.userGroupSyncInFlight) {
      logger.debug('[ServerSync] User group sync already in-flight, skipping duplicate')
      return
    }
    // Fire-and-forget with in-flight tracking
    this.userGroupSyncInFlight = this.runUserGroupSyncInBackground().finally(() => {
      this.userGroupSyncInFlight = null
    })
  }

  /**
   * Internal: Run user group sync in background
   * This is the actual sync logic that was previously in handleUserGroupConnected
   */
  private async runUserGroupSyncInBackground(): Promise<void> {
    // Start queuing events during sync
    this.userGroupSyncInProgress = true
    this.notifyUserGroupSyncListeners(true)
    this.eventQueue = []
    
    try {
      // POST user profile to get current state and access list
      logger.info('[ServerSync] Syncing user profile...')
      await this.httpClient.getUserProfile()
      logger.info('[ServerSync] User profile synced')

      // NOTE: userProfile.scenarioAccesses is NOT a reliable indicator of which scenarios still exist.
      // When an owner deletes a scenario, the backend currently soft-deletes the scenario header but
      // does not revoke scenario access in the user document. In that state:
      //   - GET /api/scenarios filters out deleted headers
      //   - POST /api/auth/user still includes the scenarioId in scenarioAccesses
      // If the client only uses scenarioAccesses to decide what to remove, deleted scenarios can
      // remain as stale ghosts in IndexedDB on other devices.
      // Source of truth for visibility/existence is GET /api/scenarios.
      
      // Get local scenarios
      const localScenarios = await localDB.getScenarios()
      
      // Always load all scenario headers to ensure we know about everything
      logger.info('[ServerSync] Loading scenario headers...')
      const headers = await this.httpClient.getScenarios()

      type ScenarioHeaderLike = { id: string; isDeleted?: boolean }

      // Build server-visible scenario ID set from headers.
      // This excludes deleted scenarios by contract (and by API implementation).
      const visibleServerScenarioIds = new Set(
        (headers as ScenarioHeaderLike[] | null | undefined || [])
          .filter(h => !h.isDeleted)
          .map(h => h.id)
      )

      // Remove scenarios that are not returned by the server.
      // Keep local-only scenarios (sequenceNumber === 0) pending first sync.
      for (const local of localScenarios) {
        if (local.sequenceNumber === 0) {
          logger.info(`[ServerSync] Retaining offline-created scenario ${local.id} (seq=0) pending sync`)
          continue
        }

        if (!visibleServerScenarioIds.has(local.id) && !local.isDeleted) {
          await localDB.saveScenario({ ...local, isDeleted: true })
          logger.info(`[ServerSync] Removed scenario ${local.id} - not returned by server scenario list (likely deleted or access revoked)`)
        }
      }
      
      // Add headers for new scenarios only with sequenceNumber = -1
      const localScenarioMap = new Map(localScenarios.map(s => [s.id, s]))
      const allAccessibleScenarioIds = new Set<string>()
      
      for (const header of headers) {
        // Skip deleted scenarios returned by API
        if (header.isDeleted) {
          continue
        }

        allAccessibleScenarioIds.add(header.id)
        
        if (!localScenarioMap.has(header.id)) {
          await localDB.saveScenario({
            id: header.id,
            sequenceNumber: -1,  // Not loaded yet
            name: header.name,
            description: header.description,
            createdAt: header.createdAt,
            updatedAt: header.updatedAt,
            isDeleted: false,
            role: header.role === 'owner' ? 'owner' : 'editor'
          })
          logger.info(`[ServerSync] Added new scenario header provided by server: ${header.id}`)
        } else {
          // Resurrect scenario if it was marked deleted locally but exists on server
          // This repairs data from the previous sync bug
          const local = localScenarioMap.get(header.id)
          if (local && local.isDeleted) {
            await localDB.saveScenario({ ...local, isDeleted: false })
            logger.info(`[ServerSync] Resurrected scenario ${local.id} - server indicates it is accessible`)
          }
        }
      }
      
      // Always reload scenarios and notify listeners on user group sync
      // This ensures UI is updated even when logging in on a device with stale data
      // where scenario IDs haven't changed (scenariosChanged=false)
      await this.memoryStateService.loadAllScenarios()
      await this.memoryStateService.applyPendingScenarioCommands()
      this.memoryStateService.notifyAllListeners()
      logger.info('[ServerSync] Reloaded scenario headers in memory store')

      // Offline-First Strategy: Background Hydration
      // We prioritize the most recently updated scenarios to ensure they are available offline.
      // The currently active scenario is handled separately by the UI component (useScenario hook),
      // which initiates its own high-priority subscription.
      this.startBackgroundHydration(headers)
      
    } catch (err) {
      logger.error('[ServerSync] User group sync failed:', err)
      // Don't throw - this is fire-and-forget, errors shouldn't propagate
    } finally {
      // Process all queued events (discard stale ones)
      await this.processQueuedEvents()
      this.userGroupSyncInProgress = false
      this.notifyUserGroupSyncListeners(false)
    }
  }

  /**
   * Accept an invite on the server.
   * The UI should hydrate via ScenarioHydrationCoordinator afterward.
   */
  async acceptInvite(inviteToken: string, scenarioId: string): Promise<string> {
    try {
      const data = await this.httpClient.acceptInvite(scenarioId, inviteToken)
      return data.scenarioId
    } catch (err: any) {
      if (err.status === 403) {
        const error = new Error('Wrong user')
        ;(error as any).code = 'WRONG_USER'
        throw error
      }
      if (err.status === 404) {
        throw new Error('The invitation is no longer available.')
      }
      throw new Error(err.message || 'Failed to accept invite')
    }
  }

  /**
   * Hydrate a scenario from server with server-first validation.
   * Intended to be called by the coordination layer (ScenarioHydrationCoordinator).
   *
   * @param scenarioId - Scenario to hydrate
   * @param priority - 'high' for user viewing, 'background' for prefetch
   * @throws 403 if access denied
   * @throws 404 if scenario not found
   * @throws Error for other failures
   */
  async hydrateScenario(scenarioId: string, priority: 'high' | 'background'): Promise<void> {
    logger.info(`[ServerSync] hydrateScenario(${scenarioId}, ${priority})`)

    // Wait for SignalR connection if needed (preferred path)
    if (!this.realtimeService.isConnected()) {
      const timeout = priority === 'high' ? 10000 : 30000
      logger.debug(`[ServerSync] SignalR not connected, waiting up to ${timeout}ms...`)

      try {
        await this.realtimeService.waitForConnection(timeout)
        logger.debug('[ServerSync] SignalR connected')
      } catch (_err) {
        logger.warn('[ServerSync] SignalR connection timeout, using HTTP-only hydration')
        await this.hydrateViaHttpOnly(scenarioId)
        return
      }
    }

    // Subscribe to scenario group (deduped by RealtimeService)
    await this.realtimeService.subscribeToScenario(scenarioId)

    // Ensure we actually sync now (and single-flight-dedupe if the join event also triggers sync)
    await this.handleScenarioGroupConnected(scenarioId)
  }

  /**
   * Hydrate a scenario using HTTP only (fallback when SignalR unavailable)
   */
  private async hydrateViaHttpOnly(scenarioId: string): Promise<void> {
    logger.info(`[ServerSync] Hydrating ${scenarioId} via HTTP (SignalR unavailable)`) 
    await this.handleScenarioGroupConnected(scenarioId)
    logger.info(`[ServerSync] HTTP-only hydration complete for ${scenarioId}`)
  }

  /**
   * Start background hydration of scenarios
   * Prioritizes recently updated scenarios to ensure they are available offline
   * Uses adaptive delays based on network quality
   */
  private async startBackgroundHydration(headers: any[]): Promise<void> {
    const hydrationCoordinator = await this.resolveHydrationCoordinator()

    // Sort headers by updatedAt descending (newest first)
    const sortedHeaders = [...headers].sort((a, b) => {
      const timeA = new Date(a.updatedAt).getTime()
      const timeB = new Date(b.updatedAt).getTime()
      return timeB - timeA
    })

    // Limit hydration count on slow networks to avoid overwhelming connection
    const maxToHydrate = this.isSlowNetwork() 
      ? Math.min(5, sortedHeaders.length)  // Only 5 on slow network
      : sortedHeaders.length

    logger.info(`[ServerSync] Starting background hydration for ${maxToHydrate} of ${sortedHeaders.length} scenarios (slow network: ${this.isSlowNetwork()})`)

    // Process sequentially to avoid network congestion
    // This runs in the background (fire-and-forget from the caller's perspective)
    for (let i = 0; i < maxToHydrate; i++) {
      const header = sortedHeaders[i]
      
      // Skip if marked deleted
      if (header.isDeleted) continue

      const startTime = Date.now()
      try {
        // Use coordinator for background hydration
        // This ensures consistent hydration path and status tracking
        await hydrationCoordinator.ensureScenarioReady(header.id, 'background')

        // IMPORTANT: Subscribe to SignalR group for live events independently of hydration.
        // The coordinator may short-circuit (scenario already in IndexedDB) without subscribing.
        // Without this, navigating to a scenario and back would leave it unsubscribed -
        // useScenario subscribes on mount but unsubscribes on unmount, and if no other
        // subscription holds the ref count, events stop flowing and the scenario goes stale.
        await this.realtimeService.subscribeToScenario(header.id)
        
        // Record response time for network quality detection
        this.recordResponseTime(Date.now() - startTime)
        
        // Add adaptive delay between subscriptions based on network quality
        const delay = this.getHydrationDelay()
        await new Promise(resolve => setTimeout(resolve, delay))
        
      } catch (err) {
        // Log but continue - background sync shouldn't stop for one failure
        logger.warn(`[ServerSync] Background hydration failed for scenario ${header.id}`, err)
        // On error, assume slow network
        this.recordResponseTime(5000)
      }
    }

    logger.info('[ServerSync] Background hydration cycle completed')
  }

  private async resolveHydrationCoordinator(): Promise<HydrationCoordinatorLike> {
    if (this.hydrationCoordinator) {
      return this.hydrationCoordinator
    }

    const module = await import('../coordination/scenario-hydration-coordinator')
    return module.scenarioHydrationCoordinator
  }

  /**
   * Handler: Scenario group subscription established - sync scenario data
   */
  async handleScenarioGroupConnected(scenarioId: string): Promise<void> {
    const existing = this.scenarioSyncRequests.get(scenarioId)
    if (existing) {
      await existing
      return
    }

    const promise = (async () => {
      // Queue events during sync
      this.scenarioSyncInProgress.add(scenarioId)

      try {
        // Get current scenario state (may be missing on fresh device / deep-link)
        const scenario = await localDB.getScenario(scenarioId)

        // Determine 'since' parameter for sync.
        // If we don't have a local header yet, force snapshot bootstrap.
        let since = scenario?.sequenceNumber
        if (since === undefined || since === -1) {
          // API guarantees snapshot when since < 0
          since = -1
        }

        logger.info(`[ServerSync] Syncing scenario ${scenarioId} from sequenceNumber ${since}`)

        // CRITICAL: server validation happens here
        let response
        try {
          response = await this.httpClient.syncScenario(scenarioId, since)
        } catch (err: any) {
          // DO NOT mutate local state on 403/404.
          // Let the error propagate to the caller (coordinator will translate to status).
          if (err?.status === 403) {
            logger.warn(`[ServerSync] Access denied for ${scenarioId}`)
            throw err
          }
          if (err?.status === 404) {
            logger.warn(`[ServerSync] Scenario ${scenarioId} not found`)
            throw err
          }
          throw err
        }

        // Server confirmed scenario exists and is accessible - now apply data
        if (response.snapshot) {
          logger.info(`[ServerSync] Snapshot included in sync response for ${scenarioId}, applying atomically`)

          // Determine user role for this scenario
          let userRole: 'owner' | 'editor' = 'editor'
          try {
            const userProfile = await this.httpClient.getUserProfile()
            const access = (userProfile.scenarioAccesses || []).find((a: any) => a.scenarioId === scenarioId)
            const roleCandidate = String(access?.role || '').toLowerCase()
            userRole = roleCandidate === 'owner' ? 'owner' : 'editor'
          } catch (_err) {
            // Ignore and fall back to safest default (editor)
          }

          await localDB.applyScenarioSnapshotAtomic(
            scenarioId,
            response.snapshot,
            response.events,
            userRole
          )

          await this.tabSyncCoordinator.applySnapshot(scenarioId)
          logger.info(`[ServerSync] Snapshot applied successfully for ${scenarioId}`)
        } else {
          logger.info(`[ServerSync] Applying ${response.events.length} events for ${scenarioId}`)

          for (const event of response.events) {
            await this.tabSyncCoordinator.processEvent(event)
          }

          logger.info(`[ServerSync] Applied ${response.events.length} events for ${scenarioId}`)
        }
      } catch (err) {
        logger.error(`[ServerSync] Scenario sync failed for ${scenarioId}:`, err)
        throw err
      } finally {
        try {
          await this.processQueuedEvents()
        } finally {
          this.scenarioSyncInProgress.delete(scenarioId)
        }
      }
    })()

    this.scenarioSyncRequests.set(scenarioId, promise)
    try {
      await promise
    } finally {
      this.scenarioSyncRequests.delete(scenarioId)
    }
  }

  /**
   * Handler: Message received from SignalR - queue or process event
   */
  async handleMessageReceived(event: EventPayload): Promise<void> {
    // Queue events during sync, otherwise process immediately
    if (this.userGroupSyncInProgress || this.scenarioSyncInProgress.has(event.scenarioId)) {
      logger.debug('[ServerSync] Queuing event during sync:', event.eventType)
      this.eventQueue.push(event)
    } else {
      // Process through TabSyncCoordinator (handles processing + broadcasting + memory reload)
      await this.tabSyncCoordinator.processEvent(event)
    }
  }

  /**
   * Process queued events, discarding stale ones
   */
  private async processQueuedEvents(): Promise<void> {
    const events = [...this.eventQueue]
    this.eventQueue = []
    
    logger.info(`[ServerSync] Processing ${events.length} queued events`)
    
    for (const event of events) {
      try {
        // Check if event is stale
        const scenario = await localDB.getScenario(event.scenarioId)
        
        if (scenario && event.sequenceNumber <= scenario.sequenceNumber) {
          logger.debug(`[ServerSync] Discarding stale event: seq ${event.sequenceNumber} <= local ${scenario.sequenceNumber}`)
          continue
        }
        
        // Process event through coordinator
        await this.tabSyncCoordinator.processEvent(event)
      } catch (err) {
        logger.error('[ServerSync] Error processing queued event:', err)
      }
    }
  }

  // ==================== Network Quality Detection ====================

  /**
   * Record a response time for network quality tracking
   */
  private recordResponseTime(ms: number): void {
    this.lastResponseTimes.push(ms)
    if (this.lastResponseTimes.length > ServerSyncService.MAX_RESPONSE_TIME_SAMPLES) {
      this.lastResponseTimes.shift()
    }
  }

  /**
   * Get average response time from recent samples
   */
  private getAverageResponseTime(): number {
    if (this.lastResponseTimes.length === 0) return 0
    return this.lastResponseTimes.reduce((a, b) => a + b, 0) / this.lastResponseTimes.length
  }

  /**
   * Detect if network is slow based on response times
   */
  private isSlowNetwork(): boolean {
    return this.getAverageResponseTime() > ServerSyncService.SLOW_NETWORK_THRESHOLD
  }

  /**
   * Get adaptive delay for background hydration based on network quality
   */
  private getHydrationDelay(): number {
    const avgResponse = this.getAverageResponseTime()
    if (avgResponse === 0) return 200 // Default for no samples
    if (avgResponse > 3000) return 2000 // Very slow - 2 second delay
    if (avgResponse > 1500) return 1000 // Slow - 1 second delay
    if (avgResponse > 500) return 500  // Medium - 500ms delay
    return 200 // Fast - 200ms delay
  }
}

export const serverSyncService = new ServerSyncService()

// Expose to window for debugging
if (typeof window !== 'undefined') {
  ;(window as any).debugSync = {
    forceClearPending: () => serverSyncService.forceClearPendingItems(),
    getPendingItems: () => serverSyncService.getPendingItems(),
    sync: () => serverSyncService.sync()
  }
}

